// Excelファイルの読み替え情報

import { IYearMonth } from '../date';

/**
 * 初期設定可能なファイルの種類(配列)
 */
export const inputFileTypes = ['Inventory', 'SalesResults', 'Arrival'] as const;

/**
 * 初期設定可能なファイルの種類
 */
export type InputFileType = (typeof inputFileTypes)[number];

/**
 * 読み替え可能なヘッダー
 * @param Warehouse - 倉庫
 * @param WarehouseNumber - 倉庫番号
 * @param SupplierName - 仕入先名
 * @param SupplierNumber - 仕入先番号
 * @param BusinessNumber - 商品番号
 * @param BusinessName - 商品名
 * @param Package - 容量
 * @param CustomerName - 売上先名
 * @param CustomerNumber - 売上先番号
 * @param SalesDestinationName - 送り先名
 * @param SalesDestinationNumber - 送り先番号
 * @param InventoryValue - 在庫数量
 * @param SalesResultsValue - 販売実績数量
 * @param ArrivalValue - 入荷実績数量
 * @param Manufacturer - 製造社
 * @param EndUser - エンドユーザー
 */
export type AlterableHeader =
  | 'Warehouse'
  | 'WarehouseNumber'
  | 'SupplierName'
  | 'SupplierNumber'
  | 'BusinessNumber'
  | 'BusinessName'
  | 'Package'
  | 'CustomerName'
  | 'CustomerNumber'
  | 'SalesDestinationName'
  | 'SalesDestinationNumber'
  | 'InventoryValue'
  | 'SalesResultsValue'
  | 'ArrivalValue'
  | 'Manufacturer'
  | 'EndUser';

/**
 * 読み替え可能なヘッダー情報
 */
export type AlterableHeaderInfos = Record<
  AlterableHeader,
  {
    headerName: string;
    value: string;
  }
>;

/**
 * 年月情報の取得パターン
 */
export type SelectYearMonthPattern = 'Inlined' | 'InHeader';

/**
 * 読み替え可能なヘッダーのService
 */
export class AlterableHeaderService {
  static readonly inventoryAlterableHeaderList: AlterableHeader[] = [
    'Warehouse',
    'WarehouseNumber',
    'SupplierName',
    'SupplierNumber',
    'BusinessNumber',
    'BusinessName',
    'Package',
    'InventoryValue',
    'Manufacturer',
  ];

  static readonly salesResultsAlterableHeaderList: AlterableHeader[] = [
    'Warehouse',
    'WarehouseNumber',
    'SupplierName',
    'SupplierNumber',
    'BusinessNumber',
    'BusinessName',
    'Package',
    'CustomerName',
    'CustomerNumber',
    'SalesDestinationName',
    'SalesDestinationNumber',
    'SalesResultsValue',
    'EndUser',
  ];

  static readonly arrivalAlterableHeaderList: AlterableHeader[] = [
    'Warehouse',
    'WarehouseNumber',
    'SupplierName',
    'SupplierNumber',
    'BusinessNumber',
    'BusinessName',
    'Package',
    'ArrivalValue',
    'Manufacturer',
  ];

  /**
   * 可変ヘッダー情報を取得する
   * @param inputFileType - ファイルの種類
   * @param selectYearMonthPattern - 年月情報の取得パターン
   * @param registerExcelInfo - 可変ヘッダー情報
   */
  static readonly getAlterableYearMonthHeaderValue = (
    inputFileType: InputFileType,
    selectYearMonthPattern: SelectYearMonthPattern,
    registerExcelInfo: {
      header: AlterableHeader;
      value: string;
    }[]
  ): string => {
    if (selectYearMonthPattern === 'Inlined') return ''; // Inlinedの場合は可変ヘッダー情報を返さない
    switch (inputFileType) {
      case 'Inventory':
        return registerExcelInfo.find((info) => info.header === 'InventoryValue')?.value ?? '';
      case 'SalesResults':
        return registerExcelInfo.find((info) => info.header === 'SalesResultsValue')?.value ?? '';
      case 'Arrival':
        return registerExcelInfo.find((info) => info.header === 'ArrivalValue')?.value ?? '';
    }
  };
}

/**
 * Excelファイルの読み替え情報
 */
export interface RegisterExcelInfo {
  headerLineNumber: number;
  alternateHeaderInfos: {
    header: AlterableHeader;
    value: string;
    unused: boolean | undefined;
  }[];
  selectYearMonthPattern: SelectYearMonthPattern;
  inlinedYearMonthHeaderName: string | undefined;
  inHeaderYearMonthInfo:
    | {
        startColumnNumber: string;
        endColumnNumber: string;
        start: IYearMonth;
        end: IYearMonth;
      }
    | undefined;
}
